import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './RootLayout.css';
import './components/Money.css';

import { useEffect, useState } from "react";
import { Outlet, useLocation, Link } from 'react-router-dom';

import { useAuth } from "./contexts/AuthContext";
import { useImpersonation } from "./contexts/ImpersonationContext";
import TermsBanner from "./components/TermsBanner";
import TermsConsentModal from "./components/TermsConsentModal";
import ImpersonationBanner from "./components/ImpersonationBanner";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import { MarketingNav, MarketingFooter } from "./components/MarketingChrome";
import { MarketingLink } from "./components/MarketingLink";

// Routes that render on the host-facing slate app shell. Any
// other path (landing, marketing, auth, legal, 404, OG card) gets
// the cream-paper marketing surface — falling through to marketing
// for unknown URLs is intentional so the editorial 404 inherits
// the right chrome.
const APP_PREFIXES = ["/games", "/players", "/members", "/admin", "/feedback", "/profile", "/logout", "/mobile-preview"];

// /og is rendered without the marketing nav/footer chrome — it
// needs to fill the 1200×630 viewport completely so Playwright
// can capture it as the OpenGraph image.
const BARE_PATHS = new Set(["/og"]);

function RootLayout() {
    const { pathname } = useLocation();
    const { refreshAuth } = useAuth();

    // Re-verify the session on every navigation so auth state stays in sync
    // after transitions that update the cookie outside login()/logout() — e.g.
    // registration redirecting into the app. checkAuth is a cheap 204, and the
    // context no-ops when the value is unchanged.
    useEffect(() => {
        refreshAuth();
    }, [pathname, refreshAuth]);

    const isBare = BARE_PATHS.has(pathname);
    const isAppRoute = APP_PREFIXES.some(
        (p) => pathname === p || pathname.startsWith(p + "/"),
    );

    if (isBare) {
        return (
            <div className="app-shell surface-marketing surface-bare">
                <Outlet />
            </div>
        );
    }

    if (!isAppRoute) {
        return (
            <div className="app-shell surface-marketing">
                <MarketingNav />
                <Outlet />
                <MarketingFooter />
            </div>
        );
    }

    return (
        <div className="app-shell has-sidebar">
            <AppSidebar pathname={pathname} />
            <div className="app-main">
                <TermsBanner />
                <ImpersonationBanner />
                <EmailVerificationBanner />
                <Outlet />
            </div>
            <TermsConsentModal />
        </div>
    );
}

// Primary destinations. Members was renamed to Players; the /members
// path still matches so its redirect keeps the active state correct.
const PRIMARY_LINKS = [
    { to: "/games", label: "Games", glyph: "≡", match: ["/games"] },
    { to: "/players", label: "Players", glyph: "◔", match: ["/players", "/members"] },
];

function AppSidebar({ pathname }) {
    const { isAuthenticated } = useAuth();
    const { me } = useImpersonation();
    const isAdmin = !!me?.user?.isAdmin;
    const [open, setOpen] = useState(false);
    const close = () => setOpen(false);

    const isActive = (prefixes) =>
        prefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));

    return (
        <>
            <div className="app-topbar">
                <button
                    type="button"
                    className="app-topbar-toggle"
                    aria-label="Open menu"
                    aria-expanded={open}
                    onClick={() => setOpen(true)}
                >
                    <span className="app-topbar-toggle-bars" aria-hidden="true" />
                </button>
                <MarketingLink to="/" className="app-topbar-brand" onClick={close}>
                    <img
                        src="/poker-ledger-logo-half.svg"
                        alt="Poker Ledger"
                        className="app-topbar-logo"
                    />
                </MarketingLink>
            </div>

            {open && (
                <div
                    className="app-sidebar-scrim"
                    onClick={close}
                    aria-hidden="true"
                />
            )}

            <aside className={`app-sidebar${open ? " is-open" : ""}`}>
                <MarketingLink to="/" className="app-sidebar-brand" onClick={close}>
                    <img
                        src="/poker-ledger-logo-full.svg"
                        alt="Poker Ledger"
                        className="app-sidebar-logo"
                    />
                </MarketingLink>

                {isAuthenticated && (
                    <nav className="app-sidebar-nav">
                        {PRIMARY_LINKS.map((l) => (
                            <SidebarLink
                                key={l.to}
                                {...l}
                                active={isActive(l.match)}
                                onNavigate={close}
                            />
                        ))}
                    </nav>
                )}

                <div className="app-sidebar-foot">
                    <div className="app-sidebar-section">Account</div>
                    {isAuthenticated ? (
                        <>
                            <SidebarLink to="/profile" label="Profile" glyph="◍" active={isActive(["/profile"])} onNavigate={close} />
                            <SidebarLink to="/feedback" label="Feedback" glyph="✎" active={isActive(["/feedback"])} onNavigate={close} />
                            {isAdmin && (
                                <SidebarLink to="/admin" label="Admin" glyph="⚙" active={isActive(["/admin"])} onNavigate={close} />
                            )}
                            <SidebarLink to="/logout" label="Log out" glyph="→" onNavigate={close} />
                        </>
                    ) : (
                        <>
                            <SidebarLink to="/sign-up" label="Sign up" glyph="＋" active={isActive(["/sign-up"])} onNavigate={close} />
                            <SidebarLink to="/login" label="Log in" glyph="→" active={isActive(["/login"])} onNavigate={close} />
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}

function SidebarLink({ to, label, glyph, active, onNavigate }) {
    return (
        <Link
            to={to}
            className={`app-sb-link${active ? " is-active" : ""}`}
            onClick={onNavigate}
        >
            <span className="app-sb-glyph" aria-hidden="true">{glyph}</span>
            <span className="app-sb-label">{label}</span>
        </Link>
    );
}

export default RootLayout;
